/**
 * Monthly actual vs. budget: dashed budget outline with the actual bar inside it. Elapsed months
 * are buttons that drill into that month's transactions.
 */
import { type KeyboardEvent, useCallback } from 'react'

import type { MonthlyPoint } from '@/api/types'
import { formatMoney } from '@/lib/format'
import { useElementWidth } from '@/lib/useElementWidth'

import styles from './TrendChart.module.css'

/** Default and minimum drawing width; the chart then follows its container's width. */
const DEFAULT_W = 720
const MIN_W = 360
const H = 200
const PAD = { l: 30, r: 10, t: 14, b: 24 }
const INNER_H = H - PAD.t - PAD.b

interface TrendChartProps {
  monthly: MonthlyPoint[]
  year: number
  onSelectMonth: (month: string) => void
}

interface BarGeometry {
  cx: number
  barW: number
  budgetTop: number
  actualTop: number
  /** y of zero: bars grow up from it, credits (negative months) down. */
  baseY: number
}

function TrendBar({
  point,
  geometry,
  year,
  onSelectMonth,
}: {
  point: MonthlyPoint
  geometry: BarGeometry
  year: number
  onSelectMonth: (month: string) => void
}) {
  const handleClick = useCallback(() => onSelectMonth(point.month), [onSelectMonth, point.month])
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<SVGGElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onSelectMonth(point.month)
      }
    },
    [onSelectMonth, point.month],
  )
  const actual = point.actual ?? 0
  const tone = actual === 0 && point.budget === 0 ? styles.empty : actual <= point.budget ? styles.good : styles.bad
  const label = `${point.month} ${year} — Actual ${formatMoney(actual)} / Budget ${formatMoney(point.budget)}. View transactions`
  return (
    <g
      className={styles.actual}
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <title>{label}</title>
      <rect
        className={`${styles.bar} ${tone}`}
        x={geometry.cx - geometry.barW / 2 + 4}
        y={Math.min(geometry.actualTop, geometry.baseY)}
        width={Math.max(0, geometry.barW - 8)}
        height={Math.abs(geometry.baseY - geometry.actualTop)}
        rx={1.5}
      />
    </g>
  )
}

export function TrendChart({ monthly, year, onSelectMonth }: TrendChartProps) {
  const [ref, W] = useElementWidth<SVGSVGElement>(DEFAULT_W, MIN_W)
  const INNER_W = W - PAD.l - PAD.r
  const maxVal = Math.max(1, ...monthly.map((m) => Math.max(m.actual ?? 0, m.budget)))
  const minVal = Math.min(0, ...monthly.map((m) => Math.min(m.actual ?? 0, m.budget)))
  const slot = INNER_W / Math.max(1, monthly.length)
  const barW = Math.min(46, slot * 0.5)
  const y = (value: number) => PAD.t + INNER_H * ((maxVal - value) / (maxVal - minVal))
  const baseY = y(0)

  return (
    <svg
      ref={ref}
      className={styles.chart}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMinYMid meet"
      aria-label="Actual vs. budget by month"
    >
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <line
          key={f}
          className={styles.axisLine}
          x1={PAD.l}
          y1={PAD.t + INNER_H * (1 - f)}
          x2={W - PAD.r}
          y2={PAD.t + INNER_H * (1 - f)}
        />
      ))}
      <line className={styles.axisLine} x1={PAD.l} y1={baseY} x2={W - PAD.r} y2={baseY} />
      {monthly.map((point, i) => {
        const cx = PAD.l + slot * i + slot / 2
        const budgetTop = y(point.budget)
        const geometry: BarGeometry = { cx, barW, budgetTop, actualTop: y(point.actual ?? 0), baseY }
        return (
          <g key={point.month}>
            <rect
              className={styles.budget}
              x={cx - barW / 2}
              y={Math.min(budgetTop, baseY)}
              width={barW}
              height={Math.abs(baseY - budgetTop)}
              rx={2}
            >
              <title>{`${point.month} ${year} — Budget ${formatMoney(point.budget)}`}</title>
            </rect>
            {point.upcoming || point.actual === null ? (
              <text className={styles.valLabel} x={cx} y={budgetTop - 6} textAnchor="middle">
                upcoming
              </text>
            ) : (
              <TrendBar point={point} geometry={geometry} year={year} onSelectMonth={onSelectMonth} />
            )}
            <text className={styles.colLabel} x={cx} y={H - 6} textAnchor="middle">
              {point.month}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
